import { Component, computed, input, model, output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { OrganizationModel } from '../../../../core/models/data/organization.model';
import { NavItem } from '../../../../core/models/ui/profile-nav-item.model';
import { OrganizationService } from '../../../../core/services/organization.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { AvatarComponent } from '../../../../parts/avatar/avatar.component';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { OrganizationStatuses } from '../../../../core/enums/data.enums';
import { OrganizationsMessagingConstants } from '../../../../core/constants/messaging.constants';
import { OrganizationSettingModel } from '../../../../core/models/data/organization-setting.model';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { IfHasChildPermissionDirective } from '../../../../core/directives/if-has-child-permission/if-has-child-permission.directive';
import { OrganizationPermission } from '../../../../core/enums/permissions.enums';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { ResponsiveTabsComponent } from '../../../../parts/responsive-tabs/responsive-tabs.component';

@Component({
  selector: 'ms-organization-profile-header',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, ConfirmationModalComponent, InfoModalComponent, AvatarComponent, IfHasPermissionDirective, IfHasChildPermissionDirective, TooltipDirective, ResponsiveTabsComponent],
  templateUrl: './organization-profile-header.component.html',
})
export class OrganizationProfileHeaderComponent {
  public organization = model<OrganizationModel | null>();
  public organizationSettings = model<OrganizationSettingModel | null>();
  public isLoading = input(false);
  public navItems = input<NavItem[]>();
  public tabChanged = output<string>();
  public organizationUpdated = output<void>();

  public OrganizationStatuses = OrganizationStatuses;
  public OrganizationPermission = OrganizationPermission;
  public OrganizationsMessagingConstants = OrganizationsMessagingConstants;
  public isSubmitting = false;

  public avatarIdentity = computed(() => {
    const org = this.organization();
    if (!org) return null;
    return {
      organizationId: org.organizationId,
      organizationName: org.name,
      avatarFileId: org.avatarFileId
    };
  });

  public confirmationConfirmFunction: Function = () => {};
  public confirmationCancelFunction: Function = () => {};

  @ViewChild('organizationProfileHeaderConfirmationModalRef') confirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('organizationProfileHeaderInfoModalRef') infoModalRef!: InfoModalComponent;

  constructor(
    private organizationService: OrganizationService,
    private bootstrapService: BootstrapService
  ) { }

  public onTabClick(tabId: string): void {
    this.tabChanged.emit(tabId);
  }

  public onAvatarChanged(updated: { avatarFileId?: string }): void {
    const current = this.organization();
    if (!current) return;
    this.organization.set({
      ...current,
      avatarFileId: updated.avatarFileId
    });
    this.organizationUpdated.emit();
  }

  // #region Avatar Removal
  public onAvatarRemoved(): void {
    this.confirmationModalRef.confirmButtonText.set("Delete");
    this.confirmationModalRef.cancelButtonText.set("Cancel");
    this.confirmationModalRef.confirmButtonClassName.set("btn-primary");
    this.confirmationModalRef.modalType.set(ModalTypes.Question);
    this.confirmationModalRef.message.set(OrganizationsMessagingConstants.ORGANIZATIONS_DELETE_AVATAR());
    this.confirmationModalRef.enableNotes.set(false);
    this.confirmationModalRef.isNoteRequired.set(false);
    this.confirmationModalRef.enableTypeToConfirm.set(false);

    this.confirmationConfirmFunction = this.removeAvatarConfirmed;
    this.confirmationCancelFunction = this.removeAvatarCancelled;

    this.bootstrapService.manageModal("#organizationProfileHeaderConfirmationModal", ModalActions.Show);
  }

  public removeAvatarConfirmed = async (): Promise<void> => {
    const organization = this.organization();
    if (!organization) return;

    this.isSubmitting = true;
    try {
      const response = await this.organizationService.updateOrganization(organization.organizationId, {
        name: organization.name,
        clearAvatarFileId: true
      });

      this.bootstrapService.manageModal("#organizationProfileHeaderConfirmationModal", ModalActions.Hide);

      if (response.success && response.data) {
        this.organization.set({ ...organization, avatarFileId: undefined });
        this.organizationUpdated.emit();
        this.infoModalRef.modalType.set(ModalTypes.Success);
        this.infoModalRef.message.set(OrganizationsMessagingConstants.ORGANIZATIONS_DELETE_AVATAR_SUCCESS());
      } else {
        this.infoModalRef.modalType.set(ModalTypes.Failure);
        this.infoModalRef.message.set(response.message || OrganizationsMessagingConstants.ORGANIZATIONS_ERROR_DELETE_AVATAR());
      }

      this.bootstrapService.manageModal("#organizationProfileHeaderInfoModal", ModalActions.Show);
    } finally {
      this.isSubmitting = false;
    }
  }

  public removeAvatarCancelled = (): void => {
    this.bootstrapService.manageModal("#organizationProfileHeaderConfirmationModal", ModalActions.Hide);
  }
  // #endregion Avatar Removal

  // #region Activation Actions
  public openActivationConfirmationModal(): void {
    const organization = this.organization();
    if (!organization) return;

    const isDeactivating = organization.status === OrganizationStatuses.Active;
    const confirmationMessage = (isDeactivating
      ? OrganizationsMessagingConstants.ORGANIZATIONS_DEACTIVATE_CONFIRMATION()
      : OrganizationsMessagingConstants.ORGANIZATIONS_REACTIVATE_CONFIRMATION())
      .replaceValuesWithObject({ organizationName: organization.name });

    this.confirmationModalRef.modalType.set(ModalTypes.Question);
    this.confirmationModalRef.message.set(confirmationMessage);
    this.confirmationModalRef.enableNotes.set(false);
    this.confirmationModalRef.isNoteRequired.set(false);

    this.confirmationModalRef.enableTypeToConfirm.set(true);
    this.confirmationModalRef.typeToConfirmText.set(isDeactivating
      ? OrganizationsMessagingConstants.ORGANIZATIONS_DEACTIVATE_TYPE_TO_CONFIRM()
      : OrganizationsMessagingConstants.ORGANIZATIONS_REACTIVATE_TYPE_TO_CONFIRM());

    this.confirmationModalRef.confirmButtonText.set(isDeactivating
      ? OrganizationsMessagingConstants.ORGANIZATIONS_DEACTIVATE_ACTION_LABEL()
      : OrganizationsMessagingConstants.ORGANIZATIONS_REACTIVATE_ACTION_LABEL());
    this.confirmationModalRef.cancelButtonText.set('Cancel');
    this.confirmationModalRef.confirmButtonClassName.set(isDeactivating ? 'btn-danger' : 'btn-primary');

    this.confirmationConfirmFunction = this.confirmedOrganizationStatusConfirmation;
    this.confirmationCancelFunction = this.onCancelActivation;

    this.bootstrapService.manageModal("#organizationProfileHeaderConfirmationModal", ModalActions.Show);
  }

  public confirmedOrganizationStatusConfirmation = async (_note: string | undefined) => {
    const organization = this.organization();
    if (!organization) return;

    this.isSubmitting = true;
    try {
      const activate = organization.status === OrganizationStatuses.Inactive;
      
      const response = await this.organizationService.setActivation(organization.organizationId, activate);

      if (response.success === false) {
        this.infoModalRef.modalType.set(ModalTypes.Failure);
      } else {
        this.infoModalRef.modalType.set(ModalTypes.Success);
        this.organizationUpdated.emit();
      }
      
      this.infoModalRef.message.set(response.message);
      
      this.bootstrapService.manageModal("#organizationProfileHeaderInfoModal", ModalActions.Show);
      this.bootstrapService.manageModal("#organizationProfileHeaderConfirmationModal", ModalActions.Hide);
    } finally {
      this.isSubmitting = false;
    }
  }

  public onCancelActivation = () => {
    this.bootstrapService.manageModal("#organizationProfileHeaderConfirmationModal", ModalActions.Hide);
  }
  // #endregion Activation Actions
}
